import { escapeRegExp } from 'lodash';

import {
  APP_HOST,
  APP_ORIGIN,
  escapeMarkdown,
  MENTION_BLOCKER,
  UNTOUCHED,
  WORD_CHARACTER,
} from '@/features/mention/mention-links';

import { type WikiPerson } from '../people-wiki.types';

type NamedGroup = { name: string };

const PERSON_PATHNAME = /^\/people\/([^/]+)$/;
const GROUP_PATHNAME = /^\/groups\/([^/]+)$/;

export type ParsedPersonMentionUrl =
  | { kind: 'none' }
  | { kind: 'person'; personId: string }
  | { kind: 'group'; groupName: string };

export const personMentionUrl = (personId: string) =>
  `${APP_ORIGIN}/people/${encodeURIComponent(personId)}`;

// Group names may hold characters `encodeURIComponent` leaves as they are and a markdown link destination can't, such as an unmatched `)`
const groupMentionUrl = (groupName: string) =>
  `${APP_ORIGIN}/groups/${encodeURIComponent(groupName).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
  )}`;

export function parsePersonMentionUrl(href: string): ParsedPersonMentionUrl {
  try {
    const url = new URL(href, APP_ORIGIN);
    if (url.protocol !== 'https:' || url.host !== APP_HOST) {
      return { kind: 'none' };
    }

    const person = url.pathname.match(PERSON_PATHNAME);
    if (person) {
      return { kind: 'person', personId: decodeURIComponent(person[1]) };
    }
    const group = url.pathname.match(GROUP_PATHNAME);
    if (group) {
      return { kind: 'group', groupName: decodeURIComponent(group[1]) };
    }
    return { kind: 'none' };
  } catch {
    return { kind: 'none' };
  }
}

/**
 * Rewrites `@<full name>` into a markdown link to a person or group URL, which
 * the renderer shows as a chip. Code, links and URLs are left alone.
 * Matches are case-sensitive, end at a word boundary, prefer longer names, and
 * resolve duplicate names to the first person in `people`, or to a person over
 * a group.
 */
export const createPersonMentionLinker = (
  people: readonly WikiPerson[],
  groups: readonly NamedGroup[] = []
) => {
  const urlsByName = new Map<string, string>();
  for (const person of people) {
    if (person.name && !urlsByName.has(person.name)) {
      urlsByName.set(person.name, personMentionUrl(person.id));
    }
  }
  for (const { name } of groups) {
    if (name && !urlsByName.has(name)) {
      urlsByName.set(name, groupMentionUrl(name));
    }
  }
  if (urlsByName.size === 0) return (text: string) => text;

  const names = [...urlsByName.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  const mention = `(?<!${MENTION_BLOCKER})@(?:${names.join('|')})(?!${WORD_CHARACTER})`;
  const pattern = new RegExp(`${UNTOUCHED}|${mention}`, 'gu');

  return (text: string) =>
    text.replace(pattern, (match) => {
      if (!match.startsWith('@')) return match;
      const url = urlsByName.get(match.slice(1));
      if (!url) return match;
      return `[${escapeMarkdown(match)}](${url})`;
    });
};
